import React, { useCallback, useEffect, useState } from "react";
import AdminPageLayout from "../../components/AdminPageLayout";
import { useTheme } from "../../contexts/ThemeContext";
import appSettingsService from "../../services/appSettingsService";
import {
  AppSetting,
  AppSettingHistoryEntry,
  AppSettingScopeSchema,
  AppSettingScopeSummary,
} from "../../types/appSettings";
import HistoryTab from "./AppSettings/HistoryTab";
import ScopeList from "./AppSettings/ScopeList";
import SettingsTab from "./AppSettings/SettingsTab";
import { RefreshCw } from "lucide-react";

const AppSettingsPage: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const [scopes, setScopes] = useState<AppSettingScopeSummary[]>([]);
  const [selectedScope, setSelectedScope] = useState<string | null>(null);
  const [schema, setSchema] = useState<AppSettingScopeSchema | null>(null);
  const [setting, setSetting] = useState<AppSetting | null>(null);
  const [history, setHistory] = useState<AppSettingHistoryEntry[]>([]);
  const [activeTab, setActiveTab] = useState<"settings" | "history">("settings");

  const [loadingScopes, setLoadingScopes] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchScopes = useCallback(async () => {
    try {
      setLoadingScopes(true);
      setError(null);
      const res = await appSettingsService.getScopes();
      setScopes(res.scopes);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch scopes");
    } finally {
      setLoadingScopes(false);
    }
  }, []);

  const fetchScopeDetail = useCallback(async (scope: string) => {
    try {
      setLoadingDetail(true);
      setError(null);
      const [schemaRes, settingRes, historyRes] = await Promise.all([
        appSettingsService.getScopeSchema(scope),
        appSettingsService.getSettings(scope),
        appSettingsService.getHistory(scope),
      ]);
      setSchema(schemaRes);
      setSetting(settingRes);
      setHistory(historyRes.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch scope details");
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    fetchScopes();
  }, [fetchScopes]);

  const handleSelectScope = (scope: string) => {
    setSelectedScope(scope);
    setActiveTab("settings");
    fetchScopeDetail(scope);
  };

  const handleRefresh = () => {
    if (selectedScope) {
      fetchScopeDetail(selectedScope);
    } else {
      fetchScopes();
    }
  };

  const handleSavedOrRestored = () => {
    if (selectedScope) fetchScopeDetail(selectedScope);
  };

  const borderClass = isDarkMode ? "border-rp-separator" : "border-gray-200";

  return (
    <AdminPageLayout
      title="App Settings"
      description="Browse scopes, view current values, and update settings per scope schema."
      actions={
        <button
          onClick={handleRefresh}
          className={`flex items-center gap-2 text-sm px-4 py-2 rounded-md border transition-colors ${
            isDarkMode
              ? "border-rp-separator text-rp-grey/50 hover:bg-rp-surface-raised hover:text-rp-grey"
              : "border-gray-300 text-gray-500 hover:bg-gray-50"
          }`}
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      }
    >
      {error && (
        <div className="mb-4 text-sm text-red-500 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {loadingScopes ? (
        <div className={`text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}>
          Loading scopes...
        </div>
      ) : (
        <div className="flex gap-6">
          <ScopeList
            scopes={scopes}
            selectedScope={selectedScope}
            onSelect={handleSelectScope}
            isDarkMode={isDarkMode}
          />

          <div className="flex-1 min-w-0">
            {!selectedScope ? (
              <div
                className={`flex items-center justify-center h-72 rounded-lg border border-dashed text-sm ${
                  isDarkMode
                    ? `${borderClass} text-rp-grey/40 bg-rp-surface`
                    : "border-gray-300 text-gray-400 bg-white"
                }`}
              >
                Select a scope to view and edit settings
              </div>
            ) : loadingDetail ? (
              <div className={`text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}>
                Loading settings...
              </div>
            ) : schema && setting ? (
              <>
                {/* Tabs */}
                <div className={`flex border-b mb-5 ${borderClass}`}>
                  {(["settings", "history"] as const).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-5 py-2.5 text-sm font-medium capitalize border-b-2 transition-colors ${
                        activeTab === tab
                          ? "text-rp-amber border-rp-amber"
                          : `${isDarkMode ? "text-rp-grey/40" : "text-gray-400"} border-transparent hover:${isDarkMode ? "text-rp-grey" : "text-gray-700"}`
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {activeTab === "settings" && (
                  <SettingsTab
                    schema={schema}
                    setting={setting}
                    onSaved={handleSavedOrRestored}
                    isDarkMode={isDarkMode}
                  />
                )}

                {activeTab === "history" && (
                  <HistoryTab
                    scope={schema.scope}
                    history={history}
                    onRestored={handleSavedOrRestored}
                    isDarkMode={isDarkMode}
                  />
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
};

export default AppSettingsPage;
